import { useEffect, useRef, useState } from "react";
import { Bell, BellOff } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import NotificationsHelpDialog from "@/components/admin/NotificationsHelpDialog";

/**
 * Poll `/api/quotes` and `/api/careers` every 30 s while the admin dashboard
 * is open. When the counts increase we notify the admin.
 *
 * Key points:
 * - The baseline is established on the FIRST poll (not at mount time), so
 *   existing records are NEVER announced as "new". Only records that arrive
 *   AFTER the dashboard is open trigger notifications.
 * - Native browser `Notification`, tab title badge, in-app toast and a short
 *   audio beep fire together so the admin is alerted through multiple senses.
 * - The badge auto-resets when the user focuses the tab.
 * - A toggle in the header enables/disables the whole watcher.
 */
export default function NewSubmissionsWatcher({ onNewSubmissions }) {
  const [enabled, setEnabled] = useState(true);
  const [permission, setPermission] = useState(typeof Notification !== "undefined" ? Notification.permission : "default");
  const [unread, setUnread] = useState(0);
  const [helpOpen, setHelpOpen] = useState(false);

  // null means "not initialised yet" → first poll will only set the baseline
  // without firing any notification for pre-existing records.
  const quotesCountRef = useRef(null);
  const appsCountRef = useRef(null);
  const originalTitleRef = useRef(typeof document !== "undefined" ? document.title : "");

  // Restore title on unmount
  useEffect(() => {
    return () => { if (typeof document !== "undefined") document.title = originalTitleRef.current; };
  }, []);

  // Update tab title badge whenever unread count changes
  useEffect(() => {
    if (typeof document === "undefined") return;
    document.title = unread > 0 ? `(${unread}) ${originalTitleRef.current}` : originalTitleRef.current;
  }, [unread]);

  // Reset unread badge whenever the user focuses the tab
  useEffect(() => {
    const onFocus = () => setUnread(0);
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  // Polling loop
  useEffect(() => {
    if (!enabled) return;
    let stopped = false;
    let timer = null;

    const beep = () => {
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        const ctx = new Ctx();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain); gain.connect(ctx.destination);
        osc.frequency.value = 880;
        osc.type = "sine";
        gain.gain.setValueAtTime(0.0001, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.2, ctx.currentTime + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.4);
        osc.start(); osc.stop(ctx.currentTime + 0.42);
        setTimeout(() => ctx.close(), 600);
      } catch { /* audio may be blocked */ }
    };

    const notify = (title, body) => {
      if (typeof Notification !== "undefined" && Notification.permission === "granted" && document.visibilityState !== "visible") {
        try { new Notification(title, { body, icon: "/favicon.ico", tag: "gh-admin" }); }
        catch { /* ignore */ }
      }
      toast.success(title, { description: body });
      beep();
    };

    const poll = async () => {
      try {
        const [qr, ar] = await Promise.all([api.get("/quotes"), api.get("/careers")]);
        const qCount = (qr.data || []).length;
        const aCount = (ar.data || []).length;

        // First poll → just seed the baseline, do NOT notify for existing rows.
        if (quotesCountRef.current === null || appsCountRef.current === null) {
          quotesCountRef.current = qCount;
          appsCountRef.current = aCount;
        } else {
          const newQuotes = qCount - quotesCountRef.current;
          const newApps = aCount - appsCountRef.current;
          if (newQuotes > 0) {
            notify(
              `${newQuotes} ${newQuotes > 1 ? "nuove richieste" : "nuova richiesta"} preventivo`,
              "Controlla il tab Richieste per rispondere al cliente.",
            );
            setUnread((u) => u + newQuotes);
          }
          if (newApps > 0) {
            notify(
              `${newApps} ${newApps > 1 ? "nuove candidature" : "nuova candidatura"}`,
              "Controlla il tab Candidature per visualizzare i dettagli.",
            );
            setUnread((u) => u + newApps);
          }
          // Aggiorna subito le tabelle della dashboard (oltre alla notifica)
          if ((newQuotes > 0 || newApps > 0) && typeof onNewSubmissions === "function") {
            onNewSubmissions();
          }
          quotesCountRef.current = qCount;
          appsCountRef.current = aCount;
        }
      } catch { /* silent — retry next tick */ }
      if (!stopped) timer = setTimeout(poll, 30000);
    };

    // Kick off immediately so the baseline is established right away.
    poll();
    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
    };
  }, [enabled]);

  const askOrToggle = async () => {
    if (typeof Notification === "undefined") {
      toast.error("Il tuo browser non supporta le notifiche push");
      return;
    }
    // If the browser already granted permission, just toggle the watcher.
    if (Notification.permission === "granted") {
      setPermission("granted");
      const next = !enabled;
      setEnabled(next);
      toast.info(next ? "Notifiche attivate" : "Notifiche disattivate");
      return;
    }
    // If explicitly denied, we cannot re-prompt — open the help dialog so
    // the user can follow browser-specific unblock instructions.
    if (Notification.permission === "denied") {
      setHelpOpen(true);
      return;
    }
    // "default" → ask the browser for permission.
    try {
      const p = await Notification.requestPermission();
      setPermission(p);
      if (p === "granted") {
        setEnabled(true);
        toast.success("Notifiche attivate", { description: "Riceverai un avviso ogni volta che arriva una nuova richiesta." });
      } else if (p === "denied") {
        toast.warning("Notifiche rifiutate");
      } else {
        toast.info("Permesso non concesso");
      }
    } catch {
      toast.error("Impossibile richiedere il permesso — riprova in un browser aggiornato");
    }
  };

  const active = enabled && permission === "granted";
  const label = permission === "denied"
    ? "Notifiche bloccate"
    : active
      ? "Notifiche ON"
      : permission === "granted"
        ? "Notifiche OFF"
        : "Attiva notifiche";

  return (
    <>
      <button
        type="button"
        onClick={askOrToggle}
        data-testid="notifications-toggle"
        title={
          permission === "denied"
            ? "Notifiche bloccate — click per vedere come sbloccarle"
            : active
              ? "Notifiche attive (click per disattivare)"
              : "Attiva le notifiche push per nuove richieste"
        }
        className={`inline-flex items-center gap-2 px-3 py-2.5 font-mono text-[10px] uppercase tracking-widest border transition-colors ${
          active
            ? "border-primary text-primary hover:bg-primary hover:text-primary-foreground"
            : permission === "denied"
              ? "border-destructive/50 text-destructive/80 hover:bg-destructive/5 cursor-pointer"
              : "border-border text-muted-foreground hover:border-foreground hover:text-foreground"
        }`}
      >
        {active ? <Bell className="w-3.5 h-3.5" /> : <BellOff className="w-3.5 h-3.5" />}
        <span className="hidden sm:inline">{label}</span>
        {permission === "denied" && (
          <span className="hidden sm:inline font-bold ml-1 underline underline-offset-2" data-testid="notifications-help-inline">Come sbloccare?</span>
        )}
      </button>
      <NotificationsHelpDialog open={helpOpen} onOpenChange={setHelpOpen} />
    </>
  );
}
